// The simulation's tick, as the client has it now. No three.js here.
//
// The server states its tick in the sun message (about 33 Hz) and the rate it is running at in the stats message. A body of the client's own (the observer, orbit.ts) lives in ticks, not in
// seconds, so it needs the tick this frame: the last message's, plus the ticks the simulation has run since at the rate it states. That is all there is to "no time warp": the observer's
// time is the simulation's, a faster sky is a higher tick rate, a paused simulation (rate 0) holds the observer still.
//
// A new message corrects the estimate by working off the difference over CORRECT_S, never by stepping it back: the tick this returns never decreases (until the simulation restarts, which a
// message with an earlier tick than the last, or a long silence, says).

/** The correction a message brings is worked off over about this long (s). */
export const CORRECT_S = 1.5;
/** The tick is not carried on past this long after the last message (s): a dropped connection does not run the clock away. */
export const MAX_LEAD_S = 3.0;
/** A message further than this many seconds of simulation from the estimate is a jump (a restart, a seek), not noise: the clock takes it at once. */
const JUMP_S = 20;

export class SimClock {
  private tick = NaN;          // the last message's tick
  private at = 0;              // when it arrived (ms)
  private rate = 0;            // ticks per second
  private corr = 0;            // correction still to work off (ticks), at `corrAt`
  private corrAt = 0;
  private last = NaN;          // the last value returned

  /** The simulation's rate (ticks per second) as the stats message states it; 0 when paused or unknown. */
  setRate(tps: number): void { this.rate = tps > 0 && Number.isFinite(tps) ? tps : 0; }
  get ticksPerSecond(): number { return this.rate; }

  private raw(ms: number): number {
    const lead = Math.min(Math.max(0, (ms - this.at) / 1000), MAX_LEAD_S);
    return this.tick + this.rate * lead + this.corr * Math.exp(-Math.max(0, ms - this.corrAt) / 1000 / CORRECT_S);
  }

  /** A sun message's tick, which arrived at `ms`. The same message again is ignored. */
  note(tick: number, ms: number): void {
    if (!Number.isFinite(tick)) return;
    if (tick === this.tick && ms === this.at) return;
    if (!Number.isFinite(this.tick) || tick < this.tick || Math.abs(tick - this.raw(ms)) > JUMP_S * Math.max(this.rate, 1)) {
      this.tick = tick; this.at = ms; this.corr = 0; this.corrAt = ms; this.last = NaN;
      return;
    }
    const was = this.raw(ms);
    this.tick = tick; this.at = ms;
    this.corr = was - tick; this.corrAt = ms;
  }

  /** The tick now (ms: the frame's time); NaN until a message has come. Never less than it was. */
  now(ms: number): number {
    if (!Number.isFinite(this.tick)) return NaN;
    const t = this.raw(ms);
    this.last = Number.isFinite(this.last) ? Math.max(this.last, t) : t;
    return this.last;
  }
}
